import { useEffect, useState } from 'react';
import axios from 'axios';

import type { IPublicCertificatePaymentContext } from '@share/common-types';
import { getPublicCertificatePaymentContextAsync } from '@share/api';

interface UsePublicCertificatePaymentContextParams {
  token: string | undefined;
}

interface UsePublicCertificatePaymentContextResult {
  context: IPublicCertificatePaymentContext | null;
  isLoading: boolean;
  isError: boolean;
}

export function usePublicCertificatePaymentContext({
  token,
}: UsePublicCertificatePaymentContextParams): UsePublicCertificatePaymentContextResult {
  const [context, setContext] = useState<IPublicCertificatePaymentContext | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(token?.trim()));
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    const trimmedToken = token?.trim();

    if (!trimmedToken) {
      setContext(null);
      setIsLoading(false);
      setIsError(true);
      return;
    }

    const abortController = new AbortController();
    let isActive = true;

    setContext(null);
    setIsLoading(true);
    setIsError(false);

    getPublicCertificatePaymentContextAsync(trimmedToken, { signal: abortController.signal })
      .then((response) => {
        if (!isActive) {
          return;
        }
        setContext(response.data);
        setIsLoading(false);
        setIsError(false);
      })
      .catch((error: unknown) => {
        if (!isActive) {
          return;
        }
        if (axios.isCancel(error)) {
          return;
        }
        setContext(null);
        setIsLoading(false);
        setIsError(true);
      });

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [token]);

  return { context, isLoading, isError };
}
